// ============================================
// ServiciosExpediente.js — pestaña "Servicios" del expediente (CONTRATISTAS-EXTERNOS Bloque 3)
// ============================================
// Lista los trabajos programados de ESTE expediente (misma data que el Calendario de campo: `programaciones`) con el
// estado del trabajo, la gente interna y, persona por persona, la respuesta de las cuadrillas externas
// (propuesto / aceptado / rechazado con motivo / sin respuesta / en curso / terminado / cerrado).
// "Programar trabajo" abre el MISMO formulario del calendario con el expediente ya fijo.
// Quien no tiene permiso de calendario ve el aviso que manda el servidor (403), no una pantalla vacia.

import React, { useCallback, useEffect, useState } from 'react';
import { CalendarPlus, Users, HardHat, Lock, XCircle, Clock, MapPin } from 'lucide-react';
import { useUsuario } from '../../context/UsuarioContext';
import { authHeaders } from '../../utils/storage';
import { API_BASE } from '../../config/api';
import { ProgramarDesdeExpediente } from '../../CalendarioCampo';
import { PP_EXP, COL_EXP as COL } from '../helpersExpedientes';

const TIPOS = { levantamiento: 'Levantamiento', instalacion: 'Instalación', servicio: 'Servicio', reparacion: 'Reparación', otro: 'Otro' };
const ESTADOS = {
  programado: { label: 'Programado', color: '#5c9adb' }, en_curso: { label: 'En curso', color: '#1D9E75' }, hecho: { label: 'Hecho', color: '#10b981' },
  reagendado: { label: 'Reagendado', color: '#a86b1e' }, cancelado: { label: 'Cancelado', color: '#e05c5c' },
};
// Estados POR PERSONA de la cuadrilla externa (decision de Ricardo: aceptacion por persona, no por orden).
export const ESTADO_PERSONA = {
  propuesto: { label: 'Propuesto', color: '#5E666E', bg: '#EEF0F2' },
  aceptado: { label: 'Aceptado', color: '#1E7A4C', bg: '#E6F4EC' },
  rechazado: { label: 'Rechazado', color: '#B42318', bg: '#FEE2E2' },
  sin_respuesta: { label: 'Sin respuesta', color: '#9A5B00', bg: '#FFF4D6' },
  en_curso: { label: 'En curso', color: '#1D9E75', bg: '#E6F4EC' },
  terminado: { label: 'Terminado', color: '#2E7AC4', bg: '#EAF2FB' },
  cerrado: { label: 'Cerrado', color: '#3D4248', bg: '#E5E7EB' },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-MX', { timeZone: 'America/Mexico_City', weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
const mismoDia = (a, b) => new Date(a).toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }) === new Date(b).toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });

async function api(path, opts = {}) {
  const r = await fetch(`${API_BASE}${path}`, { ...opts, headers: { ...authHeaders(), 'Content-Type': 'application/json', ...(opts.headers || {}) } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(d.error || `Error ${r.status}`); e.status = r.status; throw e; }
  return d;
}

export function ChipPersona({ estado }) {
  const d = ESTADO_PERSONA[estado] || ESTADO_PERSONA.propuesto;
  return <span style={{ fontSize: 11, fontWeight: 700, color: d.color, background: d.bg, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>{d.label}</span>;
}

export default function ServiciosExpediente({ expediente, esCelular }) {
  const { puede, accesoTotal } = useUsuario();
  const puedeProgramar = accesoTotal || puede('operaciones.programar') || puede('operaciones.calendario');
  const puedeCerrar = accesoTotal || puede('operaciones.programar');
  const [lista, setLista] = useState(null);
  const [error, setError] = useState('');
  const [sinAcceso, setSinAcceso] = useState(false);
  const [programando, setProgramando] = useState(false);
  const [ocupado, setOcupado] = useState(null);

  const cargar = useCallback(async () => {
    try { const r = await api(`/api/programaciones?oportunidad_id=${expediente.id}`); setLista(r.programaciones || []); setError(''); setSinAcceso(false); }
    catch (e) { if (e.status === 403) setSinAcceso(true); else setError(e.message); setLista([]); }
  }, [expediente.id]);
  useEffect(() => { cargar(); }, [cargar]);

  const cerrar = async (p) => {
    if (!window.confirm('¿Cerrar este trabajo? La cuadrilla externa deja de ver el sitio y las personas quedan en "cerrado".')) return;
    setOcupado(p.id);
    try { await api(`/api/programaciones/${p.id}/cerrar`, { method: 'POST' }); await cargar(); }
    catch (e) { setError(e.message); }
    setOcupado(null);
  };
  const cancelar = async (p) => {
    if (!window.confirm('¿Cancelar este trabajo? Libera a todas las personas.')) return;
    setOcupado(p.id);
    try { await api(`/api/programaciones/${p.id}`, { method: 'DELETE' }); await cargar(); }
    catch (e) { setError(e.message); }
    setOcupado(null);
  };

  const card = { background: '#fff', border: `1px solid ${COL.borde}`, borderRadius: 12, padding: esCelular ? 12 : 16, fontFamily: PP_EXP };
  const btn = (primario) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '0 14px', borderRadius: 10, fontFamily: PP_EXP, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', border: primario ? 'none' : `1px solid ${COL.borde}`, background: primario ? COL.principal : '#fff', color: primario ? '#fff' : COL.gris });

  if (sinAcceso) {
    return (
      <div style={{ ...card, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <Lock size={20} color={COL.grisClaro} />
        <div style={{ fontSize: 13.5, color: COL.gris, lineHeight: 1.5 }}>Los trabajos de campo de este expediente los ve quien tiene el permiso <b>Calendario</b> (operaciones.calendario). Pide a tu coordinador que te lo marque o que programe la visita.</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: PP_EXP }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: COL.grisClaro, lineHeight: 1.4 }}>
          Trabajos programados de este expediente. Son los mismos del <b>Calendario de campo</b>: lo que se agende aquí aparece allá y al revés.
        </div>
        {puedeProgramar && (
          <button type="button" onClick={() => setProgramando(true)} style={btn(true)}><CalendarPlus size={16} /> Programar trabajo</button>
        )}
      </div>
      {error && <div style={{ background: '#FEE2E2', color: '#B42318', padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 13 }}>{error}</div>}
      {lista === null && <div style={{ color: COL.grisClaro, fontSize: 13 }}>Cargando…</div>}
      {lista && lista.length === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: 36, color: COL.grisClaro }}>
          <HardHat size={34} style={{ opacity: 0.4 }} />
          <div style={{ marginTop: 8, fontSize: 14 }}>Este expediente no tiene trabajos programados.</div>
          {puedeProgramar && <div style={{ fontSize: 12.5, marginTop: 4 }}>Usa "Programar trabajo" para agendar un levantamiento, una instalación o un servicio.</div>}
        </div>
      )}
      <div style={{ display: 'grid', gap: 10 }}>
        {(lista || []).map(p => {
          const est = ESTADOS[p.estado] || { label: p.estado, color: COL.grisClaro };
          const rango = mismoDia(p.fecha_inicio, p.fecha_fin) ? `${fmt(p.fecha_inicio)} → ${new Date(p.fecha_fin).toLocaleTimeString('es-MX', { timeZone: 'America/Mexico_City', hour: '2-digit', minute: '2-digit' })}` : `${fmt(p.fecha_inicio)} → ${fmt(p.fecha_fin)}`;
          const externos = p.integrantes || [];
          const abierto = p.estado !== 'cancelado' && !p.cerrada_at;
          return (
            <div key={p.id} style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: COL.grisClaro, textTransform: 'uppercase', letterSpacing: 0.4 }}>{TIPOS[p.tipo] || p.tipo}</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: est.color, background: `${est.color}1a`, borderRadius: 6, padding: '2px 8px' }}>{est.label}</span>
                {p.cerrada_at && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#3D4248', background: '#E5E7EB', borderRadius: 6, padding: '2px 8px' }}>Cerrado por Beep</span>}
                <span style={{ marginLeft: 'auto', fontSize: 11.5, color: COL.grisClaro }}>#{p.id}</span>
              </div>
              {p.titulo && <div style={{ fontSize: 15, fontWeight: 700, color: COL.oscuro, marginTop: 4 }}>{p.titulo}</div>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: COL.gris, marginTop: 6 }}><Clock size={15} color={COL.grisClaro} /> {rango}</div>
              {p.oportunidad_direccion && <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, color: COL.gris, marginTop: 4 }}><MapPin size={15} color={COL.grisClaro} style={{ flexShrink: 0, marginTop: 2 }} /> <span>{p.oportunidad_direccion}</span></div>}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, color: COL.gris, marginTop: 6 }}>
                <Users size={15} color={COL.grisClaro} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>{(p.tecnicos || []).length ? <><b>Técnicos:</b> {p.tecnicos.map(t => t.nombre).join(', ')}</> : <span style={{ color: COL.grisClaro }}>Sin técnico interno</span>}</span>
              </div>
              {externos.length > 0 && (
                <div style={{ marginTop: 8, borderTop: `1px dashed ${COL.borde}`, paddingTop: 8 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: COL.grisClaro, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Cuadrilla externa · respuesta por persona</div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {externos.map(g => (
                      <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
                        <HardHat size={14} color="#8b5cf6" />
                        <span style={{ fontWeight: 600, color: COL.oscuro }}>{g.nombre}</span>
                        <span style={{ color: COL.grisClaro, fontSize: 12 }}>{g.contratista}</span>
                        <ChipPersona estado={g.estado} />
                        {g.motivo && <span style={{ fontSize: 12, color: '#B42318' }}>— {g.motivo}</span>}
                      </div>
                    ))}
                  </div>
                  {p.instrucciones_externo && <div style={{ fontSize: 12.5, color: COL.gris, marginTop: 8, background: '#F8FAFC', borderRadius: 8, padding: '8px 10px' }}><b>Instrucciones para la cuadrilla:</b> {p.instrucciones_externo}</div>}
                </div>
              )}
              {abierto && puedeCerrar && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                  <button type="button" disabled={ocupado === p.id} onClick={() => cerrar(p)} style={btn(false)}><Lock size={15} /> Cerrar trabajo</button>
                  <button type="button" disabled={ocupado === p.id} onClick={() => cancelar(p)} style={{ ...btn(false), color: '#B42318' }}><XCircle size={15} /> Cancelar</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {programando && (
        <ProgramarDesdeExpediente
          esCelular={esCelular}
          tipoSugerido="instalacion"
          oportunidad={{ id: expediente.id, folio: expediente.folio, titulo: expediente.titulo, cliente_nombre: expediente.cliente_nombre || expediente.cliente_grupo_nombre }}
          onCerrar={() => setProgramando(false)}
          onCreada={() => { setProgramando(false); cargar(); }}
        />
      )}
    </div>
  );
}
